import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import api from '../../utils/axiosConfig';
import { getImageUrl } from '../../utils/imageUtils';

interface Project {
    _id: string;
    title: string;
    slug: string;
    subtitle?: string;
    category: string;
    shortDescription: string;
    fullDescription: string;
    logo?: string;
    banner?: string;
    image?: string;
    url?: string;
    color: string;
    objectives?: string[];
    highlights?: string[];
    results?: string[];
    overview?: string;
}

const ProjectDetails: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [project, setProject] = useState<Project | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchProject = async () => {
            if (!slug) {
                setError('Invalid project slug');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const response = await api.get(`/projects/${slug}`);
                if (response.data.success) {
                    setProject(response.data.data);
                } else {
                    setError('Project not found');
                }
            } catch (err: any) {
                console.error('Failed to fetch project:', err);
                setError(err.response?.data?.message || 'Failed to load project');
            } finally {
                setLoading(false);
            }
        };

        fetchProject();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-white">
                <PublicHeader />
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-32">
                    <div className="text-center">
                        <div className="spinner mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading project...</p>
                    </div>
                </div>
                <PublicFooter />
            </div>
        );
    }

    if (error || !project) {
        return (
            <div className="min-h-screen bg-white">
                <PublicHeader />
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-32">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-[#3B3B3B] mb-4">Project Not Found</h1>
                        <p className="text-gray-600 mb-8">{error || 'The project you\'re looking for doesn\'t exist.'}</p>
                        <Link
                            to="/projects"
                            className="inline-flex items-center px-6 py-3 bg-[#3B82F6] text-white rounded-xl hover:bg-blue-700 transition-all font-semibold"
                        >
                            Back to Projects
                        </Link>
                    </div>
                </div>
                <PublicFooter />
            </div>
        );
    }

    const colorSchemes: { [key: string]: any } = {
        blue: {
            bg: 'from-[#3B82F6] to-blue-600',
            text: 'text-[#3B82F6]',
            badge: 'bg-[#3B82F6]',
            light: 'bg-blue-50',
            border: 'border-[#3B82F6]'
        },
        orange: {
            bg: 'from-[#F59E0B] to-orange-600',
            text: 'text-[#F59E0B]',
            badge: 'bg-[#F59E0B]',
            light: 'bg-orange-50',
            border: 'border-[#F59E0B]'
        },
        purple: {
            bg: 'from-purple-500 to-purple-700',
            text: 'text-purple-600',
            badge: 'bg-purple-600',
            light: 'bg-purple-50',
            border: 'border-purple-600'
        },
        green: {
            bg: 'from-green-500 to-green-700',
            text: 'text-green-600',
            badge: 'bg-green-600',
            light: 'bg-green-50',
            border: 'border-green-600'
        },
        pink: {
            bg: 'from-pink-500 to-pink-700',
            text: 'text-pink-600',
            badge: 'bg-pink-600',
            light: 'bg-pink-50',
            border: 'border-pink-600'
        },
        indigo: {
            bg: 'from-indigo-500 to-indigo-700',
            text: 'text-indigo-600',
            badge: 'bg-indigo-600',
            light: 'bg-indigo-50',
            border: 'border-indigo-600'
        },
        cyan: {
            bg: 'from-cyan-500 to-cyan-700',
            text: 'text-cyan-600',
            badge: 'bg-cyan-600',
            light: 'bg-cyan-50',
            border: 'border-cyan-600'
        },
        violet: {
            bg: 'from-violet-500 to-violet-700',
            text: 'text-violet-600',
            badge: 'bg-violet-600',
            light: 'bg-violet-50',
            border: 'border-violet-600'
        }
    };

    const colors = colorSchemes[project.color] || colorSchemes.blue;

    const getProjectImage = () => {
        if (project.banner) return getImageUrl(project.banner);
        if (project.image) return getImageUrl(project.image);
        if (project.logo) return getImageUrl(project.logo);
        return '';
    };

    return (
        <div className="min-h-screen bg-white">
            <PublicHeader />

            {/* Hero Section */}
            <section className={`pt-32 pb-20 bg-gradient-to-br ${colors.bg} relative overflow-hidden`}>
                {getProjectImage() && (
                    <div className="absolute inset-0">
                        <img
                            src={getProjectImage()}
                            alt={project.title}
                            className="w-full h-full object-cover opacity-20"
                        />
                    </div>
                )}
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <Link
                            to="/projects"
                            className="inline-flex items-center text-white hover:text-white/80 mb-6 font-medium transition-colors"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to Projects
                        </Link>

                        <div className="mb-6">
                            <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-semibold">
                                {project.category}
                            </span>
                        </div>

                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
                            {project.title}
                        </h1>
                        {project.subtitle && (
                            <p className="text-xl sm:text-2xl text-white/90 mb-8 font-light">
                                {project.subtitle}
                            </p>
                        )}
                        {project.shortDescription && (
                            <p className="text-lg text-white/80 mb-8 max-w-3xl mx-auto">
                                {project.shortDescription}
                            </p>
                        )}
                    </div>
                </div>
            </section>

            {/* Overview Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-2xl md:text-3xl font-bold text-[#3B3B3B] mb-6">Project Overview</h2>
                        {project.overview && (
                            <div className={`${colors.light} rounded-2xl p-8 border ${colors.border} mb-8`}>
                                <p className="text-gray-700 leading-relaxed text-lg">
                                    {project.overview}
                                </p>
                            </div>
                        )}
                        {project.fullDescription && (
                            <div className="prose prose-lg max-w-none">
                                <div
                                    className="text-gray-700 leading-relaxed project-content"
                                    dangerouslySetInnerHTML={{ __html: project.fullDescription }}
                                />
                            </div>
                        )}
                        {project.url && (
                            <div className="mt-8">
                                <a
                                    href={project.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`inline-flex items-center px-6 py-3 ${colors.badge} text-white rounded-xl hover:opacity-90 transition-all font-semibold`}
                                >
                                    Visit Project
                                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Objectives Section */}
            {project.objectives && project.objectives.length > 0 && (
                <section className="py-20 bg-gray-50">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-2xl md:text-3xl font-bold text-[#3B3B3B] mb-8">Objectives</h2>
                            <div className="grid grid-cols-1 gap-4">
                                {project.objectives.map((objective, index) => (
                                    <div
                                        key={index}
                                        className="flex items-start bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100"
                                    >
                                        <div className={`w-8 h-8 ${colors.badge} rounded-full flex items-center justify-center flex-shrink-0 mr-4 mt-1`}>
                                            <span className="text-white font-bold text-sm">{index + 1}</span>
                                        </div>
                                        <p className="text-gray-700 leading-relaxed">{objective}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Highlights Section */}
            {project.highlights && project.highlights.length > 0 && (
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-2xl md:text-3xl font-bold text-[#3B3B3B] mb-8">Key Highlights</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {project.highlights.map((highlight, index) => (
                                    <div
                                        key={index}
                                        className="group bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-opacity-0 hover:-translate-y-1"
                                    >
                                        <div className="flex items-start">
                                            <div className={`w-6 h-6 rounded-full ${colors.badge} flex items-center justify-center flex-shrink-0 mr-3 mt-0.5`}>
                                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <p className="text-gray-700 leading-relaxed">{highlight}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Results Section */}
            {project.results && project.results.length > 0 && (
                <section className="py-20 bg-gray-50">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-2xl md:text-3xl font-bold text-[#3B3B3B] mb-8">Results & Impact</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {project.results.map((result, index) => (
                                    <div
                                        key={index}
                                        className={`${colors.light} p-6 rounded-2xl border ${colors.border} shadow-md`}
                                    >
                                        <div className="flex items-start">
                                            <svg className={`w-6 h-6 ${colors.text} flex-shrink-0 mr-3 mt-0.5`} fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                            <p className="text-gray-700 leading-relaxed font-medium">{result}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* CTA Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-2xl md:text-3xl font-bold text-[#3B3B3B] mb-4">Interested in Similar Solutions?</h2>
                        <p className="text-xl text-gray-600 mb-8">
                            Let us help you create impactful projects that drive real results.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#3B82F6] to-blue-600 text-white rounded-xl hover:shadow-2xl transition-all duration-300 font-semibold text-base hover:-translate-y-1"
                        >
                            Get in Touch
                            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
};

export default ProjectDetails;
